@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_cus

import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.allFromTabularCommit
import org.example.project.enginekartei.KarteiApi
import org.example.project.service.InfrastrukturApi
import org.example.project.service.GlobalManager
import org.example.project.service.mitBerechneterPlatzBelegung
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.flexWrap
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.overflow
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div

private fun buildPlatzSave(id: String, cols: List<String>, infra: All?, modultypenAktiv: Boolean, existing: All?): All? {
    val normalizedCols = if (modultypenAktiv) {
        cols
    } else {
        listOf(
            cols.getOrElse(0) { "" },
            cols.getOrElse(1) { "" },
            cols.getOrElse(2) { "" },
            cols.getOrElse(3) { "" },
            cols.getOrElse(4) { "" },
            cols.getOrElse(5) { "" },
            cols.getOrElse(6) { "" },
            cols.getOrElse(7) { "" },
            existing?.modulTyp.orEmpty(),
            existing?.belegtTyp.orEmpty(),
            existing?.vpCCG1.orEmpty(),
            existing?.vpCCG2.orEmpty(),
            cols.getOrElse(8) { "0" },
            cols.getOrElse(9) { "⬜" }
        )
    }
    val base = allFromTabularCommit(
        rowId = id,
        cols = listOf(
            id,
            normalizedCols.getOrElse(1) { "" },
            normalizedCols.getOrElse(2) { "" },
            normalizedCols.getOrElse(3) { "" },
            normalizedCols.getOrElse(4) { "" },
            normalizedCols.getOrElse(5) { "" },
            normalizedCols.getOrElse(6) { "" },
            normalizedCols.getOrElse(7) { "" },
            normalizedCols.getOrElse(8) { "" },
            normalizedCols.getOrElse(9) { "" },
            normalizedCols.getOrElse(10) { "" },
            normalizedCols.getOrElse(11) { "" },
            normalizedCols.getOrElse(12) { "0" }
        ),
        fieldIds = listOf(
            "id",
            "typ",
            "standort",
            "halle",
            "regal",
            "fach",
            "ebene",
            "platzcode",
            "modulTyp",
            "belegtTyp",
            "vpCCG1",
            "vpCCG2",
            "belegt"
        )
    )
    val platzcode = base.platzcode.ifBlank { infraCodeAusMeta(
        infra = infra,
            typ = base.typ,
        values = mapOf(
            "standort" to base.standort,
            "halle" to base.halle,
            "regal" to base.regal,
            "fach" to base.fach,
            "ebene" to base.ebene,
            "tor" to (base.tor ?: 0).toString(),
            "bereich" to base.bereich
        )
    ) }
    if (platzcode.isBlank()) return null
    return base.copy(
        id = platzcode,
        platzcode = platzcode,
        typ = normalizedCols.getOrElse(1) { "" }.ifBlank { base.typ.ifBlank { "Platz" } },
        bezeichnung = normalizedCols.getOrElse(1) { "" },
        tor = null,
        aktiv = normalizedCols.getOrElse(13) { "⬜" } != "✅",
    ).mitBerechneterPlatzBelegung()
}

private fun platzPfadKey(item: All): String =
    listOf(item.standort, item.halle, item.regal, item.fach, item.ebene).joinToString("|")

private const val PLATZ_AUSKUNFT_TYP = "Platz"
private const val WA_PLATZ_AUSKUNFT_TYP = "WAPlatz"

@Composable
fun CUSPlatzAuskunft(art: String = org.example.project.service.currentSaveArt()) {
    CUSPlatzAuskunftContent(
        art = art,
        festerPlatzTyp = PLATZ_AUSKUNFT_TYP,
        screenKey = "PlatzAuskunft",
        title = "Platzauskunft"
    )
}

@Composable
fun CUSWAPlatzAuskunft(art: String = org.example.project.service.currentSaveArt()) {
    CUSPlatzAuskunftContent(
        art = art,
        festerPlatzTyp = WA_PLATZ_AUSKUNFT_TYP,
        screenKey = "WAPlatzAuskunft",
        title = "WA Platzauskunft"
    )
}

@Composable
private fun CUSPlatzAuskunftContent(
    art: String,
    festerPlatzTyp: String,
    screenKey: String,
    title: String
) {
    val scope = rememberCoroutineScope()
    var daten by remember { mutableStateOf(mutableListOf<All>()) }
    var letzteFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    var standorte by remember { mutableStateOf<List<All>>(emptyList()) }
    var platzTypOptionen by remember { mutableStateOf<List<All>>(emptyList()) }
    var modulTypOptionen by remember { mutableStateOf<List<String>>(emptyList()) }
    var selectedStandort by remember { mutableStateOf("") }
    var selectedHalle by remember { mutableStateOf("") }
    var selectedPlatzTyp by remember { mutableStateOf("") }
    var selectedModulTyp by remember { mutableStateOf("") }
    var strukturHallen by remember { mutableStateOf<List<String>>(emptyList()) }
    var strukturRegale by remember { mutableStateOf<List<String>>(emptyList()) }
    var strukturFaecher by remember { mutableStateOf<List<String>>(emptyList()) }
    var strukturEbenen by remember { mutableStateOf<List<String>>(emptyList()) }
    var strukturTore by remember { mutableStateOf<List<String>>(emptyList()) }
    var regalVon by remember { mutableStateOf("") }
    var regalBis by remember { mutableStateOf("") }
    var fachVon by remember { mutableStateOf("") }
    var fachBis by remember { mutableStateOf("") }
    var ebeneVon by remember { mutableStateOf("") }
    var ebeneBis by remember { mutableStateOf("") }
    var torVon by remember { mutableStateOf("") }
    var torBis by remember { mutableStateOf("") }
    var infra by remember { mutableStateOf<All?>(null) }
    var modultypenAktiv by remember { mutableStateOf(false) }
    var generatePlaetze by remember { mutableStateOf<List<All>>(emptyList()) }
    var showGenerateConfirm by remember { mutableStateOf(false) }
    var generateBusy by remember { mutableStateOf(false) }
    var generateProgress by remember { mutableStateOf(0) }
    var generateResultText by remember { mutableStateOf("") }
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("yard")
    )

    suspend fun loadPlaetze() {
        daten = letzteFilter?.let { InfrastrukturApi.searchPlaetze(festerPlatzTyp, it) }.orEmpty()
            .filter { it.typ.trim().equals(festerPlatzTyp, ignoreCase = true) }
            .distinctBy { "${it.standort}|${it.halle}|${it.regal}|${it.fach}|${it.ebene}|${it.id}" }
            .sortedWith(
                compareBy<All>(
                    { it.standort.toIntOrNull() ?: Int.MAX_VALUE },
                    { it.halle.toIntOrNull() ?: Int.MAX_VALUE },
                    { it.regal.toIntOrNull() ?: Int.MAX_VALUE },
                    { it.fach.toIntOrNull() ?: Int.MAX_VALUE },
                    { it.ebene.toIntOrNull() ?: Int.MAX_VALUE }
                )
            )
            .toMutableList()
    }

    LaunchedEffect(art, festerPlatzTyp, refreshVersion) {
        val loadedModultypenAktiv = loadModultypenFeatureAktiv()
        modultypenAktiv = loadedModultypenAktiv
        val loadedInfra = InfrastrukturApi.loadInfra()
        infra = loadedInfra
        standorte = KarteiApi.loadStandorte().sortedBy { it.id.toIntOrNull() ?: 0 }
        platzTypOptionen = infraPlatzTypOptionen(loadedInfra)
            .filter { it.typ.trim().equals(festerPlatzTyp, ignoreCase = true) }
        modulTypOptionen = if (loadedModultypenAktiv) {
            InfrastrukturApi.loadModulTypen()
                .map { it.modulTyp.ifBlank { it.bezeichnung } }
                .filter { it.isNotBlank() }
                .distinct()
        } else {
            emptyList()
        }
        loadPlaetze()
    }

    LaunchedEffect(standorte) {
        if (selectedStandort.isNotBlank() && selectedStandort !in standorte.map { it.id }) {
            selectedStandort = ""
        }
    }

    LaunchedEffect(selectedStandort, standorte) {
        val standort = standorte.firstOrNull { it.id == selectedStandort }
        strukturHallen = eingebetteterStandortGeneratorRange(standort?.halleVon.orEmpty(), standort?.halleBis.orEmpty(), 2)
        strukturRegale = eingebetteterStandortGeneratorRange(standort?.regalVon.orEmpty(), standort?.regalBis.orEmpty(), 2)
        strukturFaecher = eingebetteterStandortGeneratorRange(standort?.fachVon.orEmpty(), standort?.fachBis.orEmpty(), 3)
        strukturEbenen = eingebetteterStandortGeneratorRange(standort?.ebeneVon.orEmpty(), standort?.ebeneBis.orEmpty(), 2)
        strukturTore = eingebetteterStandortGeneratorRange(standort?.toreVon.orEmpty(), standort?.toreBis.orEmpty(), 3)
        selectedHalle = ""
        regalVon = ""
        regalBis = ""
        fachVon = ""
        fachBis = ""
        ebeneVon = ""
        ebeneBis = ""
        torVon = ""
        torBis = ""
    }

    val headers = if (modultypenAktiv) listOf(
        "ID", "Platz Typ", "Standort", "Halle", "Regal", "Fach", "Ebene", "Platzcode",
        "Modul Typ", "Belegt Typ", "Frei Ccg1", "Frei Ccg2", "Belegt", "Gesperrt"
    ) else listOf(
        "ID", "Platz Typ", "Standort", "Halle", "Regal", "Fach", "Ebene", "Platzcode",
        "Belegt", "Gesperrt"
    )

    val sichtbarePlaetze = daten
    val standortOptionen = buildList {
        add(All(key = "", label = "Standort"))
        addAll(standorte.map { s ->
            All(key = s.id, label = s.standort.ifBlank { "Standort ${s.id}" })
        })
    }
    val effectivePlatzTyp = selectedPlatzTyp.ifBlank { platzTypOptionen.firstOrNull()?.key.orEmpty() }
    val selectedPlatzTypLabel = platzTypOptionen.firstOrNull { it.key == effectivePlatzTyp }?.label.orEmpty().ifBlank { festerPlatzTyp }
    val aktivePlatzFelder = infraAktiveFelder(infra, effectivePlatzTyp).toSet()
    val modultypDropdownAktiv = infraModulTypDropdownAktiv(infra, effectivePlatzTyp, modultypenAktiv)

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
            padding(12.px)
            height(100.percent)
            overflow("hidden")
            property("box-sizing", "border-box")
            property("min-height", "0")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                flexWrap(FlexWrap.Wrap)
                gap(8.px)
            }
        }) {
            Div({ style { width(240.px) } }) {
                AppDropdown(
                    options = standortOptionen,
                    selectedValue = selectedStandort,
                    onChange = { selectedStandort = it },
                    appearance = AppDropdownAppearance.Field,
                    menuMode = AppDropdownMenuMode.Overlay
                )
            }
            StandortGeneratorDropdown(
                label = "Halle",
                selectedValue = selectedHalle,
                options = strukturHallen,
                enabled = selectedStandort.isNotBlank() && strukturHallen.isNotEmpty(),
                onChange = { selectedHalle = it }
            )
            StandortGeneratorAllDropdown(
                label = "Platztyp",
                selectedValue = selectedPlatzTyp,
                options = platzTypOptionen,
                onChange = { selectedPlatzTyp = it }
            )
            if ("regal" in aktivePlatzFelder) {
                StandortGeneratorDropdown("Regal von", regalVon, strukturRegale, selectedHalle.isNotBlank() && strukturRegale.isNotEmpty()) { regalVon = it }
                StandortGeneratorDropdown("Regal bis", regalBis, strukturRegale, selectedHalle.isNotBlank() && strukturRegale.isNotEmpty()) { regalBis = it }
            }
            if ("fach" in aktivePlatzFelder) {
                StandortGeneratorDropdown("Fach von", fachVon, strukturFaecher, selectedHalle.isNotBlank() && strukturFaecher.isNotEmpty()) { fachVon = it }
                StandortGeneratorDropdown("Fach bis", fachBis, strukturFaecher, selectedHalle.isNotBlank() && strukturFaecher.isNotEmpty()) { fachBis = it }
            }
            if ("ebene" in aktivePlatzFelder) {
                StandortGeneratorDropdown("Ebene von", ebeneVon, strukturEbenen, selectedHalle.isNotBlank() && strukturEbenen.isNotEmpty()) { ebeneVon = it }
                StandortGeneratorDropdown("Ebene bis", ebeneBis, strukturEbenen, selectedHalle.isNotBlank() && strukturEbenen.isNotEmpty()) { ebeneBis = it }
            }
            if ("tor" in aktivePlatzFelder) {
                StandortGeneratorDropdown("Tor von", torVon, strukturTore, selectedHalle.isNotBlank() && strukturTore.isNotEmpty()) { torVon = it }
                StandortGeneratorDropdown("Tor bis", torBis, strukturTore, selectedHalle.isNotBlank() && strukturTore.isNotEmpty()) { torBis = it }
            }
            if (modultypDropdownAktiv) {
                StandortGeneratorDropdown(
                    label = "Modultyp",
                    selectedValue = selectedModulTyp,
                    options = modulTypOptionen,
                    onChange = { selectedModulTyp = it }
                )
            }
            Div({ style { width(150.px) } }) {
                LumaticaButton(
                    label = "Reset",
                    onClick = {
                        selectedStandort = ""
                        selectedHalle = ""
                        selectedPlatzTyp = ""
                        selectedModulTyp = ""
                        regalVon = ""
                        regalBis = ""
                        fachVon = ""
                        fachBis = ""
                        ebeneVon = ""
                        ebeneBis = ""
                        torVon = ""
                        torBis = ""
                    },
                    variant = LumaticaButtonVariant.Standard
                )
            }
            Div({ style { width(150.px) } }) {
                LumaticaButton(
                    label = "Generieren",
                    onClick = {
                        scope.launch {
                            val standortId = selectedStandort.takeIf { it.isNotBlank() } ?: return@launch
                            val hallenId = selectedHalle.takeIf { it.isNotBlank() } ?: return@launch
                            fun rangeFor(field: String, from: String, to: String): IntRange {
                                if (field !in aktivePlatzFelder) return 0..0
                                val start = from.toIntOrNull() ?: return IntRange.EMPTY
                                val end = to.toIntOrNull() ?: start
                                return start..end
                            }
                            val regalRange = rangeFor("regal", regalVon, regalBis)
                            val fachRange = rangeFor("fach", fachVon, fachBis)
                            val ebeneRange = rangeFor("ebene", ebeneVon, ebeneBis)
                            val torRange = rangeFor("tor", torVon, torBis)
                            val created = mutableListOf<All>()

                            for (regal in regalRange) {
                                for (fach in fachRange) {
                                    for (ebene in ebeneRange) {
                                        for (tor in torRange) {
                                            val platzcode = infraCodeAusMeta(
                                                infra = infra,
                                                typ = effectivePlatzTyp,
                                                values = mapOf(
                                                    "standort" to standortId,
                                                    "halle" to hallenId,
                                                    "regal" to regal.toString(),
                                                    "fach" to fach.toString(),
                                                    "ebene" to ebene.toString(),
                                                    "tor" to tor.toString()
                                                )
                                            )
                                            if (platzcode.isBlank()) continue
                                            created += All(
                                                id = platzcode,
                                                platzcode = platzcode,
                                                typ = festerPlatzTyp,
                                                bezeichnung = selectedPlatzTypLabel,
                                                standort = standortId,
                                                halle = hallenId,
                                                regal = regal.toString(),
                                                fach = fach.toString(),
                                                ebene = ebene.toString(),
                                                tor = tor.takeIf { "tor" in aktivePlatzFelder },
                                                modulTyp = if (modultypDropdownAktiv) selectedModulTyp else "",
                                                belegtTyp = "",
                                                belegt = false,
                                                aktiv = true
                                            ).mitBerechneterPlatzBelegung()
                                        }
                                    }
                                }
                            }
                            if (created.isNotEmpty()) {
                                generatePlaetze = created
                                showGenerateConfirm = true
                            }
                        }
                    },
                    variant = LumaticaButtonVariant.Action
                )
            }
        }

        Div({
            style {
                property("flex", "1 1 auto")
                property("min-height", "0")
                overflow("hidden")
            }
        }) {
            TabularScreen(
                screenKey = screenKey,
                rollenKey = screenKey,
                title = title,
                headers = headers,
                models = daten,
                data = sichtbarePlaetze.map { platz ->
                    val belegt = platz.mitBerechneterPlatzBelegung()
                        belegt.id to if (modultypenAktiv) listOf(
                            belegt.id,
                            belegt.bezeichnung.ifBlank { belegt.typ },
                            belegt.standort,
                        belegt.halle,
                        belegt.regal,
                        belegt.fach,
                        belegt.ebene,
                        belegt.platzcode,
                        belegt.modulTyp,
                        belegt.belegtTyp,
                        belegt.vpCCG1,
                        belegt.vpCCG2,
                        if (belegt.belegt) "1" else "0",
                        if (!belegt.aktiv) "✅" else "⬜"
                    ) else listOf(
                        belegt.id,
                        belegt.bezeichnung.ifBlank { belegt.typ },
                        belegt.standort,
                        belegt.halle,
                        belegt.regal,
                        belegt.fach,
                        belegt.ebene,
                        belegt.platzcode,
                        if (belegt.belegt) "1" else "0",
                        if (!belegt.aktiv) "✅" else "⬜"
                    )
                },
                editable = true,
                includeMetadata = true,
                allowRefreshButton = false,
                onSearch = { scope.launch { loadPlaetze() } },
                onSearchWithFilters = { filters ->
                    scope.launch {
                        letzteFilter = filters
                        daten = InfrastrukturApi.searchPlaetze(festerPlatzTyp, filters)
                            .distinctBy { "${it.standort}|${it.halle}|${it.regal}|${it.fach}|${it.ebene}|${it.id}" }
                            .toMutableList()
                    }
                },
                nonEditableCols = if (modultypenAktiv) {
                    setOf("Platzcode", "Frei Ccg1", "Frei Ccg2", "Belegt", "Gesperrt")
                } else {
                    setOf("Platzcode", "Belegt", "Gesperrt")
                },
                allowCreate = false,
                onDeleteRows = { ids ->
                    val targets = daten.filter { it.id in ids }
                    if (targets.isNotEmpty()) {
                        InfrastrukturApi.deletePlaetze(targets)
                    }
                    daten = daten.filterNot { it.id in ids }.toMutableList()
                },
                onCommit = { rows ->
                    scope.launch {
                        rows.forEach { (id, cols) ->
                            val existing = daten.firstOrNull { it.id == id }
                            val final = (buildPlatzSave(id, cols, infra, modultypenAktiv, existing) ?: return@forEach).copy(tor = existing?.tor)
                        if (existing == null || existing != final) {
                            if (existing != null && platzPfadKey(existing) != platzPfadKey(final)) {
                                InfrastrukturApi.deletePlatz(existing)
                            }
                            InfrastrukturApi.savePlatz(final)
                        }
                    }
                        loadPlaetze()
                    }
                }
            )
        }
        if (showGenerateConfirm) {
            GeneratorConfirmPopup(
                count = generatePlaetze.size,
                itemLabel = "Plätze",
                onCancel = {
                    showGenerateConfirm = false
                    generatePlaetze = emptyList()
                },
                onConfirm = {
                    val items = generatePlaetze
                    showGenerateConfirm = false
                    generateBusy = true
                    generateProgress = 8
                    scope.launch {
                        delay(120)
                        generateProgress = 34
                        GlobalManager.markLocalScreenWrite(screenKey, suppressMs = 6000L)
                        val saved = InfrastrukturApi.savePlaetze(items)
                        generateProgress = 76
                        loadPlaetze()
                        generateProgress = 100
                        delay(240)
                        generatePlaetze = emptyList()
                        generateResultText = "Es wurden ${saved.size} Plätze erzeugt."
                    }
                }
            )
        }
        if (generateBusy) {
            GeneratorProgressPopup(
                count = generatePlaetze.size,
                itemLabel = "Plätze",
                progress = generateProgress,
                resultMessage = generateResultText,
                onClose = {
                    generateBusy = false
                    generateResultText = ""
                }
            )
        }
    }
}
